import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { cn } from './cn.js';
import { Field } from './Field.js';
import { Input } from './Input.js';
/**
 * The box, the border and the focus ring move from the input to this row, because
 * the reveal button sits inside them. The values are Input's own (`h-11`,
 * `rounded-md`, `border-input`, `bg-card`, the `--ring` focus treatment) — this
 * control is the kit's text field with a suffix, not a second field style.
 */
const ROW_CLASSES = 'flex h-11 w-full items-stretch overflow-hidden rounded-md border border-input bg-card ' +
    'transition-colors duration-[var(--dur-quick)] ' +
    // `ring-offset-background` is named on purpose. Tailwind's ring-offset colour defaults
    // to white, so an unnamed offset paints a bright 1px hairline between the field and the
    // focus ring on every dark surface. Measured in the dashboard's dark theme before this
    // was added: --tw-ring-offset-color resolved to #fff.
    'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring ' +
    'focus-within:ring-offset-1 focus-within:ring-offset-background';
/**
 * Input keeps what it owns — the text, placeholder and disabled treatment — and
 * gives up only the box the row has taken over. Switched off rather than
 * restated, so a future change to the kit's field styling still reaches here.
 */
const INPUT_CLASSES = 'h-full w-auto min-w-0 flex-1 rounded-none border-0 bg-transparent ' +
    'focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0';
/** Destructive re-asserted on focus too, or the row's focus ring would paint over it. */
const INVALID_CLASSES = 'border-destructive focus-within:border-destructive focus-within:ring-destructive';
const TOGGLE_CLASSES = 'flex shrink-0 items-center border-l border-input bg-secondary px-3.5 text-sm font-semibold ' +
    'text-secondary-foreground transition-colors duration-[var(--dur-quick)] hover:bg-secondary/80 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ' +
    'disabled:cursor-not-allowed disabled:bg-disabled disabled:text-disabled-foreground';
/**
 * Rendered as Field's single child so Field can clone `id`, `aria-describedby`,
 * `aria-invalid` and `required` onto it; those land on the real `<input>` while
 * the wrapper keeps the border.
 */
function PasswordControl({ className, showLabel, hideLabel, disabled, id, ...props }) {
    const [visible, setVisible] = useState(false);
    const ariaInvalid = props['aria-invalid'];
    const invalid = ariaInvalid === true || ariaInvalid === 'true';
    return (_jsxs("div", { 
        // `disabled` last: a disabled control must read as disabled even when a caller
        // tints the surface to match the card its siblings sit on.
        className: cn(ROW_CLASSES, invalid && INVALID_CLASSES, className, disabled && 'bg-disabled'), children: [_jsx(Input, { id: id, type: visible ? 'text' : 'password', disabled: disabled, autoCapitalize: "none", autoCorrect: "off", spellCheck: false, className: INPUT_CLASSES, ...props }), _jsx("button", { type: "button", className: TOGGLE_CLASSES, disabled: disabled, "aria-controls": id, onClick: () => setVisible((current) => !current), children: _jsxs("span", { className: "grid justify-items-center", children: [_jsx("span", { "aria-hidden": "true", className: "invisible col-start-1 row-start-1", children: showLabel }), _jsx("span", { "aria-hidden": "true", className: "invisible col-start-1 row-start-1", children: hideLabel }), _jsx("span", { className: "col-start-1 row-start-1", children: visible ? hideLabel : showLabel })] }) })] }));
}
/** Label + password input + Show/Hide reveal + helper or error, wired for a11y by Field. */
export function PasswordField({ label, helperText, error, showLabel, hideLabel, required, className, ...props }) {
    return (_jsx(Field, { label: label, hint: helperText, error: error, required: required, children: _jsx(PasswordControl, { className: className, showLabel: showLabel, hideLabel: hideLabel, ...props }) }));
}
